import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  FirebaseTreatmentPlanService,
  TreatmentPlan,
  TreatmentGoal,
  TreatmentObjective,
  TreatmentIntervention
} from '../../../services/firebase/FirebaseTreatmentPlanService';
import {
  FirebaseDiagnosisPrescriptionService,
  FirebaseDiagnosisRecord
} from '../../../services/firebase/FirebaseDiagnosisPrescriptionService';
import COMMON_DIAGNOSES from '../../../data/icd10Diagnoses';
import { useToast } from '../../Toast';
import VoiceInputButton from '../../VoiceInputButton';

interface DiagnosisEntry {
  code: string;
  name: string;
}

interface TreatmentPlanTabProps {
  client: any;
}

const TreatmentPlanTab: React.FC<TreatmentPlanTabProps> = ({ client }) => {
  const { user, roles } = useAuth();
  const toast = useToast();
  const isAdminOrDirector = roles.includes('system_admin') || roles.includes('clinical_director');
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPlan, setEditingPlan] = useState<TreatmentPlan | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Active diagnoses from the Diagnosis tab
  const [activeDiagnoses, setActiveDiagnoses] = useState<FirebaseDiagnosisRecord[]>([]);

  // Form state
  const [diagnoses, setDiagnoses] = useState<DiagnosisEntry[]>([]);
  const [diagSearch, setDiagSearch] = useState('');
  const [showDiagDropdown, setShowDiagDropdown] = useState(false);
  const diagRef = useRef<HTMLDivElement>(null);
  const [presentingProblem, setPresentingProblem] = useState('');
  const [goals, setGoals] = useState<TreatmentGoal[]>([]);
  const [reviewDate, setReviewDate] = useState('');
  const [sessionFrequency, setSessionFrequency] = useState('Weekly');
  const [estimatedDuration, setEstimatedDuration] = useState('');

  // Subscribe to treatment plans
  useEffect(() => {
    if (!client?.id || !user?.uid) return;
    const unsub = isAdminOrDirector
      ? FirebaseTreatmentPlanService.subscribeToPatientPlans(client.id, (data) => setPlans(data))
      : FirebaseTreatmentPlanService.subscribeToProviderPatientPlans(user.uid, client.id, (data) => setPlans(data));
    return unsub;
  }, [client?.id, user?.uid, isAdminOrDirector]);

  // Subscribe to active diagnoses from Diagnosis tab
  useEffect(() => {
    if (!client?.id || !user?.uid) return;
    const unsub = isAdminOrDirector
      ? FirebaseDiagnosisPrescriptionService.subscribeToClientDiagnoses(client.id, (data) => setActiveDiagnoses(data.filter(d => d.status === 'active')))
      : FirebaseDiagnosisPrescriptionService.subscribeToDiagnoses(client.id, user.uid, (data) => setActiveDiagnoses(data.filter(d => d.status === 'active')));
    return unsub;
  }, [client?.id, user?.uid, isAdminOrDirector]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (diagRef.current && !diagRef.current.contains(e.target as Node)) {
        setShowDiagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const resetForm = () => {
    setDiagnoses([]);
    setDiagSearch('');
    setPresentingProblem('');
    setGoals([FirebaseTreatmentPlanService.createBlankGoal()]);
    setReviewDate('');
    setSessionFrequency('Weekly');
    setEstimatedDuration('');
  };

  const startCreate = () => {
    resetForm();
    // Auto-populate from active diagnoses
    if (activeDiagnoses.length > 0) {
      setDiagnoses(activeDiagnoses.map(d => ({ code: d.code, name: d.name })));
    }
    setEditingPlan(null);
    setIsCreating(true);
  };

  const startEdit = (plan: TreatmentPlan) => {
    // Support both new diagnoses[] and legacy diagnosis string
    if (plan.diagnoses && plan.diagnoses.length > 0) {
      setDiagnoses(plan.diagnoses);
    } else if (plan.diagnosis) {
      setDiagnoses([{ code: '', name: plan.diagnosis }]);
    } else {
      setDiagnoses([]);
    }
    setPresentingProblem(plan.presentingProblem);
    setGoals(plan.goals.length > 0 ? plan.goals : [FirebaseTreatmentPlanService.createBlankGoal()]);
    setReviewDate(plan.reviewDate);
    setSessionFrequency(plan.sessionFrequency || 'Weekly');
    setEstimatedDuration(plan.estimatedDuration || '');
    setEditingPlan(plan);
    setIsCreating(true);
  };

  const handleSave = async (status: 'draft' | 'active' = 'active') => {
    if (!user?.uid) return;
    if (diagnoses.length === 0 || !presentingProblem.trim() || goals.length === 0) {
      toast.warning('Missing Fields', 'Please add at least one diagnosis, presenting problem, and one goal.');
      return;
    }

    setIsSaving(true);
    try {
      const providerName = user.displayName || user.email || 'Provider';
      const diagnosisDisplay = diagnoses.map(d => d.code ? `${d.code} — ${d.name}` : d.name).join('; ');
      const planData = {
        patientId: client.id,
        providerId: user.uid,
        providerName,
        diagnosis: diagnosisDisplay,
        diagnoses,
        presentingProblem,
        goals,
        reviewDate,
        sessionFrequency,
        estimatedDuration,
        status: status as TreatmentPlan['status'],
      };

      if (editingPlan) {
        await FirebaseTreatmentPlanService.updateTreatmentPlan(
          editingPlan.id, user.uid, client.id, planData
        );
        toast.success('Plan Updated', 'Treatment plan has been updated.');
      } else {
        await FirebaseTreatmentPlanService.createTreatmentPlan(planData);
        toast.success('Plan Created', 'Treatment plan has been created.');
      }

      setIsCreating(false);
      resetForm();
    } catch (err) {
      toast.error('Save Failed', 'Could not save treatment plan.');
    } finally {
      setIsSaving(false);
    }
  };

  // Diagnosis helpers
  const addDiagnosis = (entry: DiagnosisEntry) => {
    if (diagnoses.some(d => d.code === entry.code && d.name === entry.name)) return;
    setDiagnoses(prev => [...prev, entry]);
    setDiagSearch('');
    setShowDiagDropdown(false);
  };

  const removeDiagnosis = (index: number) => {
    setDiagnoses(prev => prev.filter((_, i) => i !== index));
  };

  // Goal / Objective / Intervention helpers
  const updateGoal = (goalId: string, field: keyof TreatmentGoal, value: any) => {
    setGoals(prev => prev.map(g => g.id === goalId ? { ...g, [field]: value } : g));
  };
  const addGoal = () => setGoals(prev => [...prev, FirebaseTreatmentPlanService.createBlankGoal()]);
  const removeGoal = (goalId: string) => setGoals(prev => prev.filter(g => g.id !== goalId));

  const updateObjective = (goalId: string, objId: string, field: keyof TreatmentObjective, value: any) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return { ...g, objectives: g.objectives.map(o => o.id === objId ? { ...o, [field]: value } : o) };
    }));
  };
  const addObjective = (goalId: string) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return { ...g, objectives: [...g.objectives, FirebaseTreatmentPlanService.createBlankObjective()] };
    }));
  };
  const removeObjective = (goalId: string, objId: string) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return { ...g, objectives: g.objectives.filter(o => o.id !== objId) };
    }));
  };

  const updateIntervention = (goalId: string, objId: string, intId: string, field: keyof TreatmentIntervention, value: any) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        objectives: g.objectives.map(o => {
          if (o.id !== objId) return o;
          return { ...o, interventions: o.interventions.map(i => i.id === intId ? { ...i, [field]: value } : i) };
        })
      };
    }));
  };
  const addIntervention = (goalId: string, objId: string) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        objectives: g.objectives.map(o => {
          if (o.id !== objId) return o;
          return { ...o, interventions: [...o.interventions, FirebaseTreatmentPlanService.createBlankIntervention()] };
        })
      };
    }));
  };
  const removeIntervention = (goalId: string, objId: string, intId: string) => {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        objectives: g.objectives.map(o => {
          if (o.id !== objId) return o;
          return { ...o, interventions: o.interventions.filter(i => i.id !== intId) };
        })
      };
    }));
  };

  const statusColor = (s: string) => {
    switch (s) {
      case 'active': case 'in-progress': return '#D4A017';
      case 'achieved': case 'completed': return '#10B981';
      case 'discontinued': case 'modified': return '#EF4444';
      default: return '#6b7280';
    }
  };

  const filteredDiagnoses = COMMON_DIAGNOSES.filter(d => {
    if (diagnoses.some(sel => sel.code === d.code)) return false;
    if (!diagSearch) return true;
    const term = diagSearch.toLowerCase();
    return d.code.toLowerCase().includes(term) || d.name.toLowerCase().includes(term);
  });

  // ─── Plan list view ───
  if (!isCreating) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, color: '#F7E9A0', fontSize: '18px' }}>Treatment Plans</h3>
          <button
            onClick={startCreate}
            style={{
              background: '#D4A017',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              color: '#1f2937',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            + New Treatment Plan
          </button>
        </div>

        {plans.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            background: '#1A4D6F',
            borderRadius: '8px',
            border: '1px solid #2A5470'
          }}>
            <p style={{ color: '#d1d5db', fontSize: '16px', margin: '0 0 8px' }}>No treatment plans yet</p>
            <p style={{ color: '#d1d5db', fontSize: '14px', margin: 0 }}>
              Create a treatment plan to set goals and track progress for {client.firstName}.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {plans.map(plan => {
              const displayDiagnoses = plan.diagnoses?.length > 0
                ? plan.diagnoses.map(d => d.code ? `${d.code} — ${d.name}` : d.name).join('; ')
                : plan.diagnosis;
              return (
                <div key={plan.id} style={{
                  background: '#1A4D6F',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #2A5470'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600, color: '#FFFFFF', fontSize: '16px' }}>
                          {displayDiagnoses}
                        </span>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: statusColor(plan.status),
                          color: plan.status === 'active' ? '#1f2937' : '#FFFFFF',
                          textTransform: 'capitalize'
                        }}>
                          {plan.status}
                        </span>
                      </div>
                      <p style={{ color: '#d1d5db', fontSize: '13px', margin: 0 }}>
                        {plan.goals.length} goal{plan.goals.length !== 1 ? 's' : ''} &middot;
                        Created {plan.createdAt.toLocaleDateString()} &middot;
                        Review: {plan.reviewDate || 'Not set'}
                      </p>
                    </div>
                    <button
                      onClick={() => startEdit(plan)}
                      style={{
                        background: '#374151',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        color: '#87CEEB',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Edit
                    </button>
                  </div>

                  {plan.goals.map((goal, i) => (
                    <div key={goal.id} style={{
                      background: '#16325B',
                      borderRadius: '6px',
                      padding: '12px',
                      marginTop: '8px',
                      borderLeft: `3px solid ${statusColor(goal.status)}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500 }}>
                          Goal {i + 1}: {goal.description || '(No description)'}
                        </span>
                        <span style={{ color: statusColor(goal.status), fontSize: '12px', textTransform: 'capitalize' }}>
                          {goal.status}
                        </span>
                      </div>
                      <span style={{ color: '#d1d5db', fontSize: '12px' }}>
                        {goal.objectives.length} objective{goal.objectives.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── Create / Edit form ───
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, color: '#F7E9A0', fontSize: '18px' }}>
          {editingPlan ? 'Edit Treatment Plan' : 'New Treatment Plan'}
        </h3>
        <button
          onClick={() => { setIsCreating(false); resetForm(); }}
          style={{
            background: '#6b7280',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Diagnosis + Presenting Problem */}
        <section style={{ background: '#1A4D6F', borderRadius: '8px', padding: '20px', border: '1px solid #2A5470' }}>
          <h4 style={{ color: '#F7E9A0', margin: '0 0 16px', fontSize: '16px' }}>Diagnosis & Presenting Problem</h4>

          {/* Selected diagnoses chips */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', color: '#87CEEB', fontSize: '13px', marginBottom: '6px' }}>
              Diagnoses (ICD-10)
            </label>
            {diagnoses.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {diagnoses.map((d, i) => (
                  <span key={i} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: '#16325B',
                    border: '1px solid #6CA6C1',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: '#f9fafb'
                  }}>
                    <span style={{ color: '#58A6FF', fontWeight: 600 }}>{d.code}</span>
                    {d.name}
                    {i === 0 && <span style={{ color: '#F7E9A0', fontSize: '10px', marginLeft: '2px' }}>Primary</span>}
                    <button
                      onClick={() => removeDiagnosis(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0 2px',
                        lineHeight: 1
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Searchable dropdown */}
            <div ref={diagRef} style={{ position: 'relative' }}>
              <input
                type="text"
                value={diagSearch}
                onChange={(e) => { setDiagSearch(e.target.value); setShowDiagDropdown(true); }}
                onFocus={() => setShowDiagDropdown(true)}
                placeholder="Search by name or ICD-10 code (e.g., anxiety, F41)..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#0A2540',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px'
                }}
              />
              {showDiagDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  backgroundColor: '#16325B',
                  border: '1px solid #6CA6C1',
                  borderRadius: '0 0 6px 6px',
                  maxHeight: '240px',
                  overflowY: 'auto'
                }}>
                  {/* Show active diagnoses from Diagnosis tab first */}
                  {!diagSearch && activeDiagnoses.filter(ad => !diagnoses.some(d => d.code === ad.code)).length > 0 && (
                    <>
                      <div style={{ padding: '6px 12px', color: '#F7E9A0', fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #2A5470' }}>
                        Active Diagnoses (from chart)
                      </div>
                      {activeDiagnoses
                        .filter(ad => !diagnoses.some(d => d.code === ad.code))
                        .map(ad => (
                          <button
                            key={`active-${ad.id}`}
                            onClick={() => addDiagnosis({ code: ad.code, name: ad.name })}
                            style={{
                              width: '100%', padding: '8px 12px', background: 'none',
                              border: 'none', borderBottom: '1px solid #2A5470',
                              color: '#e5e7eb', fontSize: '14px', textAlign: 'left', cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <span style={{ color: '#10B981', fontWeight: 500 }}>{ad.code}</span>
                            <span style={{ color: '#d1d5db' }}> — </span>
                            {ad.name}
                            <span style={{ color: '#10B981', fontSize: '11px', marginLeft: '8px' }}>Active</span>
                          </button>
                        ))}
                      <div style={{ padding: '6px 12px', color: '#87CEEB', fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #2A5470' }}>
                        All Diagnoses
                      </div>
                    </>
                  )}
                  {filteredDiagnoses.slice(0, 20).map(d => (
                    <button
                      key={d.code}
                      onClick={() => addDiagnosis({ code: d.code, name: d.name })}
                      style={{
                        width: '100%', padding: '8px 12px', background: 'none',
                        border: 'none', borderBottom: '1px solid #2A5470',
                        color: '#e5e7eb', fontSize: '14px', textAlign: 'left', cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <span style={{ color: '#58A6FF', fontWeight: 500 }}>{d.code}</span>
                      <span style={{ color: '#d1d5db' }}> — </span>
                      {d.name}
                    </button>
                  ))}
                  {filteredDiagnoses.length === 0 && (
                    <div style={{ padding: '12px', color: '#d1d5db', fontSize: '13px', textAlign: 'center' }}>
                      No matches found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <label htmlFor="tp-problem" style={{ color: '#87CEEB', fontSize: '13px' }}>
                Presenting Problem
              </label>
              <VoiceInputButton onResult={text => setPresentingProblem(prev => prev + ' ' + text)} />
            </div>
            <textarea
              id="tp-problem"
              value={presentingProblem}
              onChange={(e) => setPresentingProblem(e.target.value)}
              placeholder="Why is the client seeking treatment? Symptoms, duration, impact on functioning..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px 12px',
                background: '#0A2540',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </section>

        {/* Session Info */}
        <section style={{ background: '#1A4D6F', borderRadius: '8px', padding: '20px', border: '1px solid #2A5470' }}>
          <h4 style={{ color: '#F7E9A0', margin: '0 0 16px', fontSize: '16px' }}>Session Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label htmlFor="tp-frequency" style={{ display: 'block', color: '#87CEEB', fontSize: '13px', marginBottom: '6px' }}>
                Session Frequency
              </label>
              <select
                id="tp-frequency"
                value={sessionFrequency}
                onChange={(e) => setSessionFrequency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#0A2540',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  colorScheme: 'dark'
                }}
              >
                <option value="Weekly">Weekly</option>
                <option value="Bi-weekly">Bi-weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="As needed">As needed</option>
              </select>
            </div>
            <div>
              <label htmlFor="tp-duration" style={{ display: 'block', color: '#87CEEB', fontSize: '13px', marginBottom: '6px' }}>
                Estimated Duration
              </label>
              <input
                id="tp-duration"
                type="text"
                value={estimatedDuration}
                onChange={(e) => setEstimatedDuration(e.target.value)}
                placeholder="e.g., 12 weeks"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#0A2540',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <div>
              <label htmlFor="tp-review" style={{ display: 'block', color: '#87CEEB', fontSize: '13px', marginBottom: '6px' }}>
                Next Review Date
              </label>
              <input
                id="tp-review"
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#0A2540',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  colorScheme: 'dark'
                }}
              />
            </div>
          </div>
        </section>

        {/* Goals */}
        <section style={{ background: '#1A4D6F', borderRadius: '8px', padding: '20px', border: '1px solid #2A5470' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ color: '#F7E9A0', margin: 0, fontSize: '16px' }}>Treatment Goals</h4>
            <button
              onClick={addGoal}
              style={{
                background: '#374151',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                padding: '6px 12px',
                color: '#87CEEB',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              + Add Goal
            </button>
          </div>

          {goals.map((goal, gi) => (
            <div key={goal.id} style={{
              background: '#16325B',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: gi < goals.length - 1 ? '16px' : 0,
              border: '1px solid #2A5470'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ color: '#F7E9A0', fontWeight: 600, fontSize: '14px' }}>Goal {gi + 1}</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={goal.status}
                    onChange={(e) => updateGoal(goal.id, 'status', e.target.value)}
                    style={{
                      background: '#0A2540',
                      border: '1px solid #6CA6C1',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      color: statusColor(goal.status),
                      fontSize: '12px'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="achieved">Achieved</option>
                    <option value="modified">Modified</option>
                    <option value="discontinued">Discontinued</option>
                  </select>
                  {goals.length > 1 && (
                    <button
                      onClick={() => removeGoal(goal.id)}
                      style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '16px' }}
                    >
                      x
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <VoiceInputButton onResult={text => updateGoal(goal.id, 'description', (goal.description || '') + ' ' + text)} />
              </div>
              <textarea
                value={goal.description}
                onChange={(e) => updateGoal(goal.id, 'description', e.target.value)}
                placeholder="Describe the treatment goal..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '10px',
                  background: '#0A2540',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  colorScheme: 'dark',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  marginBottom: '12px'
                }}
              />

              {/* Objectives */}
              <div style={{ marginLeft: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#87CEEB', fontSize: '13px', fontWeight: 600 }}>Objectives</span>
                  <button
                    onClick={() => addObjective(goal.id)}
                    style={{ background: 'none', border: 'none', color: '#87CEEB', cursor: 'pointer', fontSize: '12px' }}
                  >
                    + Objective
                  </button>
                </div>

                {goal.objectives.map((obj, oi) => (
                  <div key={obj.id} style={{
                    background: '#0A2540',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '8px',
                    border: '1px solid #374151'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ color: '#d1d5db', fontSize: '12px' }}>Objective {oi + 1}</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          value={obj.status}
                          onChange={(e) => updateObjective(goal.id, obj.id, 'status', e.target.value)}
                          style={{
                            background: '#16325B',
                            border: '1px solid #374151',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            color: statusColor(obj.status),
                            fontSize: '11px'
                          }}
                        >
                          <option value="not-started">Not Started</option>
                          <option value="in-progress">In Progress</option>
                          <option value="achieved">Achieved</option>
                        </select>
                        {goal.objectives.length > 1 && (
                          <button
                            onClick={() => removeObjective(goal.id, obj.id)}
                            style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '14px' }}
                          >
                            x
                          </button>
                        )}
                      </div>
                    </div>

                    <input
                      type="text"
                      value={obj.description}
                      onChange={(e) => updateObjective(goal.id, obj.id, 'description', e.target.value)}
                      placeholder="Objective description..."
                      style={{
                        width: '100%', padding: '8px', background: '#16325B',
                        border: '1px solid #374151', borderRadius: '4px',
                        color: '#f9fafb', fontSize: '13px', marginBottom: '6px'
                      }}
                    />
                    <input
                      type="text"
                      value={obj.measurable}
                      onChange={(e) => updateObjective(goal.id, obj.id, 'measurable', e.target.value)}
                      placeholder="How is this measured? (SMART criteria)"
                      style={{
                        width: '100%', padding: '8px', background: '#16325B',
                        border: '1px solid #374151', borderRadius: '4px',
                        color: '#f9fafb', fontSize: '13px', marginBottom: '8px'
                      }}
                    />

                    {/* Interventions */}
                    <div style={{ marginLeft: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#d1d5db', fontSize: '12px' }}>Interventions</span>
                        <button
                          onClick={() => addIntervention(goal.id, obj.id)}
                          style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '11px' }}
                        >
                          + Intervention
                        </button>
                      </div>
                      {obj.interventions.map((int) => (
                        <div key={int.id} style={{ display: 'flex', gap: '8px', marginBottom: '4px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={int.description}
                            onChange={(e) => updateIntervention(goal.id, obj.id, int.id, 'description', e.target.value)}
                            placeholder="Intervention (e.g., CBT thought challenging)"
                            style={{
                              flex: 1, padding: '6px 8px', background: '#1A4D6F',
                              border: '1px solid #374151', borderRadius: '4px',
                              color: '#f9fafb', fontSize: '12px'
                            }}
                          />
                          <input
                            type="text"
                            value={int.frequency || ''}
                            onChange={(e) => updateIntervention(goal.id, obj.id, int.id, 'frequency', e.target.value)}
                            placeholder="Frequency"
                            style={{
                              width: '120px', padding: '6px 8px', background: '#1A4D6F',
                              border: '1px solid #374151', borderRadius: '4px',
                              color: '#f9fafb', fontSize: '12px'
                            }}
                          />
                          {obj.interventions.length > 1 && (
                            <button
                              onClick={() => removeIntervention(goal.id, obj.id, int.id)}
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '12px' }}
                            >
                              x
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Save buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => handleSave('draft')}
            disabled={isSaving}
            style={{
              background: '#6b7280',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              color: '#fff',
              fontSize: '14px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.5 : 1
            }}
          >
            Save as Draft
          </button>
          <button
            onClick={() => handleSave('active')}
            disabled={isSaving}
            style={{
              background: '#10B981',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.5 : 1
            }}
          >
            {isSaving ? 'Saving...' : 'Activate Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanTab;
